'use client';

import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Droplets, FlaskConical, FileText, Users, AlertCircle, ChevronRight, Loader2, Shield, Activity,  } from 'lucide-react';

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

interface DemoUser {
  id: string;
  username: string;
  name: string;
  role: string;
  company: string;
  department?: string;
  password: string;
}

const DEMO_USERS: DemoUser[] = [
  { id: 'user-001', username: 'admin_kppn', name: 'Admin', role: 'Admin', company: 'PT KPPN', password: 'kppn123' },
  { id: 'user-002', username: 'abdurahman_kppn', name: 'Abdurahman', role: 'Engineer', company: 'PT KPPN', password: 'kppn123' },
  { id: 'user-003', username: 'ibrahim_kppn', name: 'Ibrahim Danto', role: 'PJO', company: 'PT KPPN', password: 'kppn123' },
  { id: 'user-004', username: 'ferryadi_kppn', name: 'Ferryadi Hanafi', role: 'Manager', company: 'PT KPPN', password: 'kppn123' },
  { id: 'user-005', username: 'ilham_kppn', name: 'Ilham Danto', role: 'Director', company: 'PT KPPN', password: 'kppn123' },
  { id: 'user-006', username: 'mike_gcsp', name: 'Mike Dapo', role: 'Owner', company: 'PT Freeport Indonesia', department: 'GCSP', password: 'owner123' },
  { id: 'user-007', username: 'adolf_gcsp', name: 'Adolf Zousa', role: 'Owner', company: 'PT Freeport Indonesia', department: 'GCSP', password: 'owner123' },
  { id: 'user-008', username: 'ananto_enviro', name: 'Ananto Saputro', role: 'Owner', company: 'PT Freeport Indonesia', department: 'Enviro', password: 'owner123' },
  { id: 'user-009', username: 'mayor_enviro', name: 'Mayor Ferry', role: 'Owner', company: 'PT Freeport Indonesia', department: 'Enviro', password: 'owner123' },
  { id: 'user-010', username: 'tenius_enviro', name: 'Tenius Tabuni', role: 'Owner', company: 'PT Freeport Indonesia', department: 'Enviro', password: 'owner123' },
];

const VALID_CREDENTIALS = DEMO_USERS.reduce<Record<string, DemoUser>>((acc, user) => {
  acc[user.username] = user;
  return acc;
}, {});

const FEATURES = [
  { id: 'feat-well', icon: Droplets, label: 'Well Monitoring' },
  { id: 'feat-quality', icon: FlaskConical, label: 'Water Quality' },
  { id: 'feat-reports', icon: FileText, label: 'Safety Reports' },
  { id: 'feat-team', icon: Users, label: 'Team Collaboration' },
];

function getPasswordStrength(password: string): { level: number; label: string } {
  if (!password) return { level: 0, label: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: 'Weak' };
  if (score === 2) return { level: 2, label: 'Medium' };
  if (score === 3) return { level: 3, label: 'Strong' };
  return { level: 4, label: 'Very Strong' };
}

export default function LoginClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { username: '', password: '', rememberMe: false } });

  const passwordValue = watch('password');
  const passwordStrength = getPasswordStrength(passwordValue);

  const onSubmit = useCallback(
    async (data: LoginFormData) => {
      setLoginError(false);
      setIsLoading(true);
      // BACKEND INTEGRATION: POST /api/auth/login with { username, password, rememberMe }
      await new Promise((r) => setTimeout(r, 1200));
      const user = VALID_CREDENTIALS[data.username];
      if (user && user.password === data.password) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('envirowell_user', JSON.stringify(user));
        }
        router.push('/main-dashboard');
      } else {
        setIsLoading(false);
        setLoginError(true);
        setShakeKey((k) => k + 1);
      }
    },
    [router]
  );

  const autofillUser = useCallback(
    (user: DemoUser) => {
      setValue('username', user.username);
      setValue('password', user.password);
      setLoginError(false);
    },
    [setValue]
  );

  const copyToClipboard = useCallback((text: string, fieldId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 1500);
    });
  }, []);

  const strengthColors = ['', 'bg-blue-200', 'bg-blue-400', 'bg-primary', 'bg-primary-dark'];
  const strengthLabels = ['', 'text-blue-300', 'text-blue-400', 'text-primary', 'text-primary'];

  return (
    <div className="min-h-screen bg-[#F5FAFF] flex items-center justify-center relative overflow-hidden">
      {/* Background geometric illustration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg className="absolute top-0 left-0 w-full h-full opacity-[0.04]" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <circle cx="200" cy="150" r="180" fill="none" stroke="#1565C0" strokeWidth="2" />
          <circle cx="200" cy="150" r="120" fill="none" stroke="#1565C0" strokeWidth="1.5" />
          <circle cx="200" cy="150" r="60" fill="none" stroke="#1565C0" strokeWidth="1" />
          <circle cx="1300" cy="750" r="220" fill="none" stroke="#1565C0" strokeWidth="2" />
          <circle cx="1300" cy="750" r="140" fill="none" stroke="#1565C0" strokeWidth="1.5" />
          <circle cx="800" cy="450" r="300" fill="none" stroke="#1565C0" strokeWidth="1" />
          <line x1="0" y1="300" x2="1440" y2="300" stroke="#1565C0" strokeWidth="0.5" strokeDasharray="8 12" />
          <line x1="0" y1="600" x2="1440" y2="600" stroke="#1565C0" strokeWidth="0.5" strokeDasharray="8 12" />
          <line x1="360" y1="0" x2="360" y2="900" stroke="#1565C0" strokeWidth="0.5" strokeDasharray="8 12" />
          <line x1="1080" y1="0" x2="1080" y2="900" stroke="#1565C0" strokeWidth="0.5" strokeDasharray="8 12" />
          <polygon points="100,500 150,420 200,500" fill="none" stroke="#1565C0" strokeWidth="1" />
          <polygon points="1200,100 1260,50 1320,100" fill="none" stroke="#1565C0" strokeWidth="1" />
          <polygon points="600,800 650,730 700,800" fill="none" stroke="#1565C0" strokeWidth="1" />
        </svg>
        <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #1565C0 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-80px] left-[-80px] w-[300px] h-[300px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #1565C0 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-[1100px] mx-auto px-4 py-8 flex flex-col xl:flex-row gap-8 items-center xl:items-start">

        {/* Left — Branding panel */}
        <div className="hidden xl:flex flex-col justify-center flex-1 pr-8 py-12">
          <div className="flex items-center gap-3 mb-8">
            <AppLogo size={48} />
            <div>
              <div className="text-xs font-semibold text-primary/70 tracking-widest uppercase">GCSP – ENVIRONMENT MONITORING WELL</div>
              <div className="text-lg font-bold text-foreground leading-tight">Groundwater Monitoring Platform</div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-foreground leading-tight mb-4">
            Environment<br />Monitoring Well
          </h1>
          <p className="text-[15px] text-muted-foreground leading-relaxed mb-10 max-w-sm">
            Comprehensive monitoring and management system for environmental compliance and groundwater well lifecycle tracking.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <div key={f.id} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-border shadow-card">
                <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <f.icon size={16} className="text-primary" />
                </div>
                <span className="text-[13px] font-600 text-foreground">{f.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-2 text-[13px] text-muted-foreground">
            <Shield size={14} className="text-primary" />
            <span>Enterprise-grade security · ISO 14001 compliant</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[13px] text-muted-foreground">
            <Activity size={14} className="text-primary" />
            <span>Real-time groundwater monitoring · PT Freeport Indonesia</span>
          </div>
        </div>

        {/* Right — Login card */}
        <div className="w-full max-w-[460px] xl:max-w-[480px]">
          {/* Mobile branding */}
          <div className="xl:hidden flex items-center gap-3 mb-6 justify-center">
            <AppLogo size={40} />
            <div>
              <div className="text-xs font-semibold text-primary/70 tracking-widest uppercase">GCSP – ENVIRONMENT MONITORING WELL</div>
              <div className="text-base font-bold text-foreground">Groundwater Monitoring Platform</div>
            </div>
          </div>

          <div
            key={shakeKey}
            className={`card-base bg-card p-8 ${shakeKey > 0 ? 'shake' : ''}`}
          >
            <div className="flex flex-col items-center mb-7">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <Users size={22} className="text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Welcome Back</h2>
              <p className="text-sm text-muted-foreground mt-1">Sign in to access your dashboard</p>
            </div>

            {loginError && (
              <div className="mb-5 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 fade-in">
                <AlertCircle size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-600 text-foreground">Invalid username or password</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Use the demo accounts below to sign in</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              <div>
                <label className="block text-[13px] font-600 text-foreground mb-1.5">
                  Username <span className="text-primary">*</span>
                </label>
                <input
                  {...register('username', { required: 'Username is required' })}
                  type="text"
                  autoComplete="username"
                  placeholder="Enter your username"
                  className={`input-base ${errors.username ? 'border-red-300 focus:border-red-400' : ''}`}
                />
                {errors.username && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={11} />
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-foreground mb-1.5">
                  Password <span className="text-primary">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('password', { required: 'Password is required' })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`input-base pr-11 ${errors.password ? 'border-red-300 focus:border-red-400' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={11} />
                    {errors.password.message}
                  </p>
                )}

                {/* Password strength indicator */}
                {passwordValue && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((lvl) => (
                        <div
                          key={`strength-bar-${lvl}`}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                            lvl <= passwordStrength.level
                              ? strengthColors[passwordStrength.level]
                              : 'bg-muted'
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-[11px] font-500 ${strengthLabels[passwordStrength.level]}`}>
                      {passwordStrength.label}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    {...register('rememberMe')}
                    type="checkbox"
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-[13px] text-muted-foreground group-hover:text-foreground transition-colors">
                    Remember me
                  </span>
                </label>
                <button type="button" className="text-[13px] font-600 text-primary hover:text-primary-dark transition-colors">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center text-[15px] py-3"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={17} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </form>

            {/* Mobile features row */}
            <div className="xl:hidden mt-5 grid grid-cols-4 gap-2">
              {FEATURES.map((f) => (
                <div key={`mob-${f.id}`} className="flex flex-col items-center gap-1.5 bg-secondary rounded-lg px-2 py-2.5">
                  <f.icon size={16} className="text-primary" />
                  <span className="text-[10px] font-600 text-primary text-center leading-tight">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Demo credentials — hidden for security */}

          <p className="text-center text-[11px] text-muted-foreground mt-4">
            © 2026 GCSP – Environment Monitoring Well · PT Freeport Indonesia Environmental Division
          </p>
        </div>
      </div>
    </div>
  );
}